import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogContent,
  Stack,
  Typography,
  CircularProgress,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Dispatch, RefObject, SetStateAction } from 'react';
import HPOFormControls from './HPOFormControls';
import { HPOSchema, HpoAPIResponse } from '@/zod_schemas';
import { getHPOColumns } from './hpo/getHPOColumns';

const CustomNoRowsOverlay = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%',
      fontSize: 16,
      color: 'text.secondary',
    }}
  >
    No data found
  </Box>
);

interface APIHPODialogProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  searchQuery: string;
  handleSearchChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  handleSearchClick: (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | React.KeyboardEvent<HTMLDivElement>,
  ) => void;
  searchFieldRef: RefObject<HTMLInputElement>;
  focusSearchField: () => void;
  handleConfirmClick: () => void;
  selectedRows: HPOSchema[];
  handleClearSelection: () => void;
  fetchedData: HpoAPIResponse;
  setSelectedRows: Dispatch<SetStateAction<HPOSchema[]>>;
  isSubmitting: boolean;
}

const APIHPODialog = ({
  open,
  setOpen,
  searchQuery,
  handleSearchChange,
  handleSearchClick,
  searchFieldRef,
  focusSearchField,
  handleConfirmClick,
  selectedRows,
  handleClearSelection,
  fetchedData,
  setSelectedRows,
  isSubmitting,
}: APIHPODialogProps) => {
  // const columnsResponse = getHPOColumns({
  //   selectAction: true,
  //   deleteAction: false,
  //   handleConfirmClick,
  //   type: 'single-hpo-api',
  //   formMode: mode,
  //   formPatientId: patientId,
  // });

  const columnsResponse = getHPOColumns({
    deleteAction: true,
  });

  const handleClose = () => {
    if (!isSubmitting) {
      setOpen(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      disableEscapeKeyDown={isSubmitting}
      PaperProps={{
        sx: {
          bgcolor: 'background.default',
          border: 'none',
        },
      }}
    >
      <DialogContent sx={{ p: 3 }}>
        <Box
          sx={{
            position: 'relative',
            opacity: isSubmitting ? 0.7 : 1,
            pointerEvents: isSubmitting ? 'none' : 'auto',
          }}
        >
          {/* Loading Overlay */}
          {isSubmitting && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: (theme) =>
                  alpha(theme.palette.background.paper, 0.88),
                borderRadius: 2,
                zIndex: 1000,
              }}
            >
              <Stack alignItems="center" spacing={2}>
                <CircularProgress size={50} />
                <Typography variant="body1" color="primary" fontWeight="medium">
                  Adding HPO terms...
                </Typography>
              </Stack>
            </Box>
          )}

          <Typography variant="h5" mb={2} color="primary">
            Human Phenotype Ontology
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            mb={2}
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
          >
            <Box sx={{ flexGrow: 1, maxWidth: '500px' }}>
              <HPOFormControls
                searchQuery={searchQuery}
                handleSearchChange={handleSearchChange}
                handleSearchClick={handleSearchClick}
                inputRef={searchFieldRef}
                focusSearchField={focusSearchField}
              />
            </Box>

            {/* Show buttons only when rows are selected */}
            {selectedRows.length > 0 && (
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => handleConfirmClick()}
                  disabled={isSubmitting}
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : null
                  }
                >
                  {isSubmitting
                    ? 'Adding...'
                    : `Add Selected (${selectedRows.length})`}
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  onClick={() => handleClearSelection()}
                  disabled={isSubmitting}
                >
                  Clear Selected ({selectedRows.length})
                </Button>
              </Stack>
            )}
          </Stack>

          <Box
            sx={{
              height: 400,
              backgroundColor: 'background.paper',
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              '& .MuiDataGrid-root': {
                border: 'none',
                fontFamily: 'Roboto, sans-serif',
                flex: 1, // fill remaining height
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'primary.light',
                fontWeight: 'bold',
              },
            }}
          >
            <DataGrid
              rows={fetchedData?.termData?.terms || []}
              columns={columnsResponse}
              //   loading={isLoading}
              initialState={{
                pagination: { paginationModel: { pageSize: 50 } },
              }}
              pageSizeOptions={[3]}
              checkboxSelection
              disableRowSelectionOnClick
              rowSelectionModel={selectedRows.map((row) => row.hpo_id)}
              onRowSelectionModelChange={(ids) => {
                if (!isSubmitting) {
                  const selectedIDs = new Set(ids);
                  const selectedRowsData = (
                    fetchedData?.termData?.terms || []
                  ).filter((row) => row.id && selectedIDs.has(row.id));
                  setSelectedRows(selectedRowsData);
                }
              }}
              getRowId={(row) => row.hpo_id}
              slots={{
                noRowsOverlay: CustomNoRowsOverlay,
              }}
            />

            {/* <DataGrid
              rows={fetchedData?.termData?.terms || []}
              columns={columnsResponse}
              loading={isLoading}
              initialState={{
                pagination: { paginationModel: { pageSize: 50 } },
              }}
              pageSizeOptions={[3]}
              checkboxSelection
              disableRowSelectionOnClick
              rowSelectionModel={selectedRows.map((row) => row.hpo_id)}
              onRowSelectionModelChange={(ids) => {
                const selectedIDs = new Set(ids);
                const selectedRowsData = (
                  fetchedData?.termData?.terms || []
                ).filter((row) => row.id && selectedIDs.has(row.id));
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                setSelectedRows(selectedRowsData as any);
              }}
              getRowId={(row) => row.hpo_id} // Use hpo_id as the unique identifier
              slots={{
                noRowsOverlay: CustomNoRowsOverlay,
              }}
            /> */}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default APIHPODialog;
